import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { orderBy } from 'lodash'
import { useAccount } from 'wagmi'
import BigNumber from 'bignumber.js'
import { useTokensPrice } from '~/hooks/useTokenPrice'
import { useAppDispatch } from '~/state/hooks'
import {
  useSpotSwapTokens,
  useSpotSwapPair,
  useTokenlonV6NetworkConfigs,
  useSpotSwapUserConfig,
} from '~/state/dex/hooks'
import { useNativeTokenBalance, useTokenBalances } from '~/state/wallet/hooks'
import { updatePendingChainId } from '~/state/user/actions'
import {
  useUpdaterChainId,
  useIsContractAddress,
  useSwitchChain,
  useIsInIframe,
} from '~/hooks'
import { useV6SDK } from '~/hooks/useV6SDK'
import { useAllSortedTokens } from '../../hooks'
import SearchBar from './SearchBar'
import NetworkList from './NetworkList'
import TokenListItem from './TokenListItem'
import TokenRecommendations from './TokenRecommendations'
import CrossTokens from './CrossTokens'
import EmptySearchResult from '../EmptySearchResult'
import TokenSelectWarning from '../TokenSelectWarning'
import { getAddress } from '@ethersproject/address'
import { zeroAddress } from 'viem'
import {
  isSameToken,
  constructTokenId,
  getIsNativeToken,
  getIsWeth,
} from '~/utils/token'
import { formatETHCrossChain } from '~/components/Exchange/helpers'
import { getDefaultToken } from './helpers'
import { TradeDirection } from '~/components/Exchange/types'
import { TokenListProps, OrderedSpotSwapToken } from './types'
import {
  StyledTokenListContainer,
  StyledTabWrapper,
  StyledTabItem,
  StyledSwitcherWrapper,
  StyledIconSwitcher,
  StyledTokenList,
} from './styled'
const useSortedSpotSwapTokens = (selectedChainId: number | null) => {
  const { address: account, chainId: currentChainId } = useAccount()
  const isSameChain = currentChainId === selectedChainId

  const tokens = useSpotSwapTokens(selectedChainId)
  const nativeTokenBalance = useNativeTokenBalance()?.toDecimalsFixed() ?? '0'
  const tokenBalances = useTokenBalances(
    isSameChain ? Object.values(tokens) : [],
  )
  const prices = useTokensPrice(isSameChain ? Object.keys(tokens) : [])

  return useMemo((): OrderedSpotSwapToken[] => {
    if (!selectedChainId) return []
    // first, sort by symbol
    const orderedTokenListBySymbol: OrderedSpotSwapToken[] = orderBy(
      Object.values(tokens),
      [(item) => item.symbol.toLowerCase()],
      ['asc'],
    )

    if (account && isSameChain) {
      // second, update value
      const nativeTokenPriceUSD = new BigNumber(prices[zeroAddress])
        .multipliedBy(nativeTokenBalance)
        .toFixed(2)
      const ethToken = {
        ...tokens[zeroAddress],
        balance: nativeTokenBalance,
        price: prices[zeroAddress],
        priceUSD: nativeTokenPriceUSD,
      }

      let orderedTokenList = orderedTokenListBySymbol.map((token) => {
        const address = getAddress(token.address)
        const isETH = address === zeroAddress

        if (isETH) return ethToken

        const balance = tokenBalances[token.address]?.toDecimalsFixed() ?? '0'
        const priceUSD = new BigNumber(prices[address])
          .multipliedBy(balance)
          .toFixed(2)

        return {
          ...token,
          balance,
          price: prices[address],
          priceUSD,
        }
      })
      // third, sort by priceUSD -> balance -> symbol
      return orderBy(
        orderedTokenList,
        [
          (item) => Number(item.priceUSD),
          (item) => Number(item.balance),
          'symbol',
        ],
        ['desc', 'desc', 'asc'],
      )
    } else return orderedTokenListBySymbol
  }, [isSameChain, prices, tokenBalances, tokens, account, selectedChainId])
}

const sortedTokensByRecent = (orderedTokensByValue, ordersHistory) => {
  if (!ordersHistory || !ordersHistory.length) return orderedTokensByValue

  const recentTokens = {}
  ordersHistory.forEach((order) => {
    const fromToken = order.fromTokenAddress.toLowerCase()
    const toToken = order.toTokenAddress.toLowerCase()
    recentTokens[fromToken] = recentTokens[fromToken]
      ? recentTokens[fromToken] + 1
      : 1
    recentTokens[toToken] = recentTokens[toToken]
      ? recentTokens[toToken] + 1
      : 1
  })

  const orderedTokensByRecent = orderedTokensByValue
    .map((token) => {
      return {
        ...token,
        tradeCounts: recentTokens[token.address.toLowerCase()] || 0,
      }
    })
    .sort((a, b) => b.tradeCounts - a.tradeCounts)

  return orderedTokensByRecent
}

const TokenList: React.FC<TokenListProps> = ({
  direction,
  selectedChainId,
  selectedHistory,
  onDismiss,
  handleSelectToken,
  setSelectedChainId,
  setDisplayTokenList,
}) => {
  const { t } = useTranslation()
  const { address: account } = useAccount()
  const { switchChain } = useSwitchChain()
  const updaterChainId = useUpdaterChainId()
  const isConnected = !!account
  const sdk = useV6SDK()
  const dispatch = useAppDispatch()

  const sortedSwapTokens = useSortedSpotSwapTokens(selectedChainId)
  const allTokens = useAllSortedTokens()
  const [inputToken, outputToken] = useSpotSwapPair()
  const networkConfigs = useTokenlonV6NetworkConfigs()
  const { recipient } = useSpotSwapUserConfig()
  const isContractAddress = useIsContractAddress(
    recipient,
    outputToken?.chainId,
  )
  const isInIframe = useIsInIframe()
  
  const [searchText, setSearchText] = useState('')

  const isSelectedAll = selectedChainId === null

  const [isSortByValue, setIsSortByValue] = useState(true)
  // v6 现在白名单机制还没实现，暂时把 tokenList 返回的 token 都当作白名单代币
  const [warningToken, setWarningToken] = useState<OrderedSpotSwapToken>()
  const [ordersHistory, setOrdersHistory] = useState([])

  const sortedSwapTokensByRecent = useMemo(
    () => sortedTokensByRecent(sortedSwapTokens, ordersHistory),
    [sortedSwapTokens, ordersHistory],
  )

  const displayTokens = useMemo((): OrderedSpotSwapToken[] => {
    if (
      (!sortedSwapTokens || !sortedSwapTokens.length) &&
      (!allTokens || !allTokens.length)
    )
      return []

    let tabResult: OrderedSpotSwapToken[] = isSelectedAll
      ? allTokens
      : isSortByValue
      ? sortedSwapTokens
      : sortedSwapTokensByRecent

    // First display symbol matches
    // Then display symbol starts with search text
    // Then display symbol contains search text
    // Then display name contains search text
    // Finally display address matches
    if (searchText) {
      const searchLowerCase = searchText.toLowerCase()
      return tabResult
        .filter((token) => {
          const { symbol, address, name } = token
          return (
            symbol.toLowerCase().includes(searchLowerCase) ||
            name?.toLowerCase().includes(searchLowerCase) ||
            address.toLowerCase() === searchLowerCase
          )
        })
        .map((token) => {
          const symbolLower = token.symbol.toLowerCase()
          const nameLower = token.name?.toLowerCase() || ''
          let score = 0

          if (symbolLower.includes(searchLowerCase)) {
            score = 100
            if (symbolLower === searchLowerCase) {
              score = 150
            } else if (symbolLower.startsWith(searchLowerCase)) {
              score = 120
            }
          } else if (nameLower.includes(searchLowerCase)) {
            score = 50
          } else if (token.address.toLowerCase() === searchLowerCase) {
            score = 10
          }
          return { ...token, score }
        })
        .sort((a, b) => b.score - a.score)
    }
    return tabResult
  }, [
    searchText,
    allTokens,
    sortedSwapTokens,
    sortedSwapTokensByRecent,
    isSortByValue,
    isSelectedAll,
    inputToken?.address,
  ])

  const onTokenSelected = async (token: SpotSwapToken) => {
    const {
      chainId: inputChainId,
      address: inputAddress,
      crossChain: inputCrossChain,
    } = inputToken
    const isNativeToken = getIsNativeToken(inputAddress)
    const isWeth = getIsWeth(inputAddress, inputChainId)

    // crossChain 是新增字段，inputToken 从 spotSwapPair 缓存中读取，可能缺少该字段。如果没有就从 tokens 中查找
    let crossChain = inputCrossChain
    if (!crossChain) {
      const inputTokenId = constructTokenId(inputChainId, inputAddress)
      crossChain = allTokens.find((item) => {
        const itemTokenId = constructTokenId(item.chainId, item.address)
        return itemTokenId === inputTokenId
      })?.crossChain
    }

    if (isNativeToken || isWeth) {
      crossChain = formatETHCrossChain(crossChain, isContractAddress)
    }

    const { chainId: selectedChainId, address: selectedAddress } = token
    const selectedTokenId = constructTokenId(selectedChainId, selectedAddress)

    const isInputDirection = direction === TradeDirection.Input
    const prevToken = isInputDirection ? inputToken : outputToken

    // 1. 无变化，直接返回
    if (isSameToken(token, prevToken)) {
      onDismiss()
      return
    }

    // 2. 判断是否是切换交易对方向
    const isChangeDirection = isInputDirection
      ? isSameToken(token, outputToken)
      : isSameToken(token, inputToken)

    // 3. 判断是否需要切换网络
    let needSwitchNetwork = false
    if (isInputDirection) {
      needSwitchNetwork = selectedChainId !== updaterChainId
    } else {
      // 3.1 如果是切换交易对方向，则判断 newInput 是否发生网络变化
      if (isChangeDirection) {
        const newInputChainId = outputToken.chainId
        needSwitchNetwork = newInputChainId !== updaterChainId
      } else {
        // 3.2 不是切换交易对方向，则判断 newOutput 是跨链还是同链。跨链要看是不是可跨链的代币，可跨链就不需要切换网络
        needSwitchNetwork =
          selectedChainId !== updaterChainId &&
          !crossChain?.some((item) => {
            const { destinationChainId, destinationToken } = item
            const tokenId = constructTokenId(
              destinationChainId,
              destinationToken,
            )
            return tokenId === selectedTokenId
          })
      }
    }

    if (needSwitchNetwork) {
      if (isConnected) {
        await switchChain({ chainId: selectedChainId })
      }
      dispatch(updatePendingChainId(selectedChainId))
    }

    let newInputToken = inputToken
    let newOutputToken = outputToken

    if (isChangeDirection) {
      newInputToken = outputToken
      newOutputToken = inputToken
      // 判断 output 代币是同链还是跨链，同链不需要处理，跨链需要判断是否是可跨链的代币，不可跨链则重置为默认代币
      const isSameChain = newInputToken.chainId === newOutputToken.chainId
      if (!isSameChain) {
        const outputTokenId = constructTokenId(
          newOutputToken.chainId,
          newOutputToken.address,
        )
        const newIsNativeToken = getIsNativeToken(newInputToken.address)
        const newIsWeth = getIsWeth(
          newInputToken.address,
          newInputToken.chainId,
        )
        let crossChain = newInputToken.crossChain
        if (newIsNativeToken || newIsWeth) {
          crossChain = formatETHCrossChain(crossChain, isContractAddress)
        }
        const isValidCross = crossChain?.some((item) => {
          const { destinationChainId, destinationToken } = item
          const tokenId = constructTokenId(destinationChainId, destinationToken)
          return tokenId === outputTokenId
        })
        if (!isValidCross) {
          newOutputToken = getDefaultToken({
            selectedChainId,
            baseToken: newInputToken,
            networkConfigs,
            tokens: allTokens,
          })
        }
      }
    } else if (needSwitchNetwork) {
      // 如果切换了网络需要寻找另一个方向的 token
      if (isInputDirection) {
        newInputToken = token
        newOutputToken = getDefaultToken({
          selectedChainId,
          baseToken: newInputToken,
          networkConfigs,
          tokens: allTokens,
        })
      } else {
        newOutputToken = token
        newInputToken = getDefaultToken({
          selectedChainId,
          baseToken: newOutputToken,
          networkConfigs,
          tokens: allTokens,
        })
      }
    } else if (isInputDirection) {
      newInputToken = token
      // 判断 output 代币是同链还是跨链，同链不需要处理，跨链需要判断是否是可跨链的代币，不可跨链则重置为默认代币
      const isSameChain = newInputToken.chainId === newOutputToken.chainId
      if (!isSameChain) {
        const outputTokenId = constructTokenId(
          newOutputToken.chainId,
          newOutputToken.address,
        )
        const newIsNativeToken = getIsNativeToken(newInputToken.address)
        const newIsWeth = getIsWeth(
          newInputToken.address,
          newInputToken.chainId,
        )
        let crossChain = newInputToken.crossChain
        if (newIsNativeToken || newIsWeth) {
          crossChain = formatETHCrossChain(crossChain, isContractAddress)
        }
        const isValidCross = crossChain?.some((item) => {
          const { destinationChainId, destinationToken } = item
          const tokenId = constructTokenId(destinationChainId, destinationToken)
          return tokenId === outputTokenId
        })
        if (!isValidCross) {
          newOutputToken = getDefaultToken({
            selectedChainId,
            baseToken: newInputToken,
            networkConfigs,
            tokens: allTokens,
          })
        }
      }
    } else {
      newOutputToken = token
    }

    handleSelectToken(newInputToken, newOutputToken)
  }

  const onWarningClose = () => {
    setWarningToken(undefined)
  }
  const onWarningSelect = (token: SpotSwapToken) => {
    onTokenSelected(token)
    onWarningClose()
  }

  useEffect(() => {
    const getOrdersHistory = async () => {
      try {
        const ordersHistory = await sdk.swap.ordersHistory({
          chainId: selectedChainId,
          userAddress: account,
          limit: 60,
          offset: 0,
        })
        setOrdersHistory(ordersHistory)
      } catch {
        setOrdersHistory([])
      }
    }
    // No switching option is provided when the selected network and the target network are different
    if (isConnected && selectedChainId === updaterChainId) {
      getOrdersHistory()
    }
  }, [isConnected, selectedChainId])

  useEffect(() => {
    if (!isConnected || selectedChainId !== updaterChainId) {
      setIsSortByValue(true)
    }
  }, [isConnected, selectedChainId])

  const isEmpty = !displayTokens || !displayTokens.length

  return (
    <StyledTokenListContainer>
      <SearchBar searchText={searchText} setSearchText={setSearchText} />
      {!isInIframe && <NetworkList
        selectedChainId={selectedChainId}
        selectedHistory={selectedHistory}
        setSelectedChainId={setSelectedChainId}
        setDisplayTokenList={setDisplayTokenList}
      />}

      {!searchText && (
        <TokenRecommendations
          selectedChainId={selectedChainId}
          onTokenSelected={onTokenSelected}
        />
      )}

      {!isInIframe && !searchText && direction === TradeDirection.Output && (
        <CrossTokens
          selectedChainId={selectedChainId}
          onSelect={onTokenSelected}
        />
      )}

      {searchText ? null : (
        <StyledTabWrapper>
          <StyledTabItem>{t('all')}</StyledTabItem>
          {isConnected && selectedChainId === updaterChainId && (
            <StyledSwitcherWrapper
              onClick={() => setIsSortByValue(!isSortByValue)}
            >
              {isSortByValue ? t('token_value') : t('recent_trades')}
              <StyledIconSwitcher />
            </StyledSwitcherWrapper>
          )}
        </StyledTabWrapper>
      )}

      {isEmpty ? (
        <EmptySearchResult />
      ) : (
        <StyledTokenList>
          {displayTokens.map((token) => (
            <TokenListItem
              key={`${token.chainId}-${token.address}`}
              token={token}
              searchText={searchText}
              showNetworkBadge={isSelectedAll}
              onSelect={() => onTokenSelected(token)}
            ></TokenListItem>
          ))}
        </StyledTokenList>
      )}

      <TokenSelectWarning
        visible={!!warningToken}
        token={warningToken}
        onClose={onWarningClose}
        onSelect={onWarningSelect}
      />
    </StyledTokenListContainer>
  )
}

export default TokenList
